import React, { useState } from "react";
import DefaultFooter from "../components/default-footer";
import DefaultHeader from "../components/default-header";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL } from "../config";

interface TimelineStep {
  id: number;
  key: string;
  title: string;
  status: "done" | "active" | "pending";
  date: string;
}

interface VisaTrackData {
  reference_number: string;
  applicant_name: string;
  visa_pack: string;
  country: string;
  status: string;
  status_label: string;
  submitted_at: string;
  updated_at: string;
  estimated_date: string;
  admin_notes?: string;
  progress: number;
  timeline: TimelineStep[];
}

const TrackVisa = () => {
  const [referenceNum, setReferenceNum] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [trackData, setTrackData] = useState<VisaTrackData | null>(null);

  const { t } = useTranslation();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!referenceNum.trim()) return;

    setLoading(true);
    setErrorMsg(null);
    setTrackData(null);

    try {
      const response = await axios.get(
        `${API_URL}/visa-applications/track/${referenceNum.trim()}`
      );
      setTrackData(response.data);
    } catch (err: any) {
      if (err.response && err.response.status === 404) {
        setErrorMsg("No visa application found with this reference number. Please check and try again.");
      } else {
        setErrorMsg("An error occurred while fetching application details. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="track-page-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');

        :root {
          --primary: #294b5a;
          --primary-dark: #1a313d;
          --gold: #b89b41;
          --cream: #faf8f4;
          --text-main: #1f2937;
          --text-muted: #4b5563;
          --border-color: #e2e8f0;
        }

        .track-page-wrapper {
          font-family: 'DM Sans', sans-serif;
          background: var(--cream);
          color: var(--text-main);
          width: 100%;
          min-height: 100vh;
          overflow-x: hidden;
        }

        /* ── HERO & SEARCH SECTION ── */
        .track-hero {
          position: relative;
          background: linear-gradient(145deg, #eef3f5 0%, #d5e2e7 100%);
          padding: 50px 24px 60px;
          text-align: center;
        }

        .track-hero-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .track-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--primary-dark);
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(8px);
          padding: 6px 18px;
          border-radius: 99px;
          border: 1px solid rgba(184, 155, 65, 0.35);
          margin-bottom: 24px;
        }

        .track-hero-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.6rem, 4.5vw, 3.8rem);
          font-weight: 400;
          color: var(--primary-dark);
          line-height: 1.15;
          margin: 0 0 16px;
        }

        .track-hero-title em {
          font-style: italic;
          color: var(--gold);
        }

        .track-hero-subtitle {
          font-size: 16px;
          color: #3d5866;
          max-width: 600px;
          margin: 0 auto 36px;
          line-height: 1.6;
        }

        /* Search Bar */
        .track-search-card {
          background: #ffffff;
          padding: 8px;
          border-radius: 16px;
          box-shadow: 0 20px 45px -10px rgba(26, 49, 61, 0.12);
          border: 1px solid rgba(26, 49, 61, 0.08);
          display: flex;
          align-items: center;
          max-width: 680px;
          margin: 0 auto;
        }

        .track-input {
          flex: 1;
          border: none;
          outline: none;
          padding: 16px 20px;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 500;
          color: var(--primary-dark);
          background: transparent;
        }

        .track-submit-btn {
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          padding: 16px 32px;
          cursor: pointer;
          transition: all 0.25s ease;
          box-shadow: 0 6px 16px rgba(41, 75, 90, 0.2);
          white-space: nowrap;
        }

        .track-submit-btn:hover {
          background: linear-gradient(135deg, #1f3a46 0%, #102028 100%);
          transform: translateY(-1px);
        }

        /* Trust Badges */
        .track-trust-badges {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 28px;
          margin-top: 28px;
          font-size: 13px;
          color: #3d5866;
          font-weight: 500;
          flex-wrap: wrap;
        }

        .track-badge-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .track-badge-icon {
          width: 16px;
          height: 16px;
          stroke: var(--gold);
          stroke-width: 2;
          fill: none;
        }

        /* Error Box */
        .track-error-box {
          max-width: 680px;
          margin: 20px auto 0;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
          padding: 14px 20px;
          border-radius: 12px;
          font-size: 14px;
          text-align: center;
        }

        /* ── DYNAMIC RESULT CARD & ROADMAP ── */
        .track-result-section {
          max-width: 900px;
          margin: -30px auto 60px;
          padding: 0 24px;
          position: relative;
          z-index: 10;
        }

        .track-result-card {
          background: #ffffff;
          border-radius: 20px;
          padding: 36px;
          box-shadow: 0 20px 40px rgba(26, 49, 61, 0.08);
          border: 1px solid rgba(184, 155, 65, 0.25);
        }

        .track-result-header {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 24px;
          margin-bottom: 32px;
        }

        .track-info-label {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted);
          margin-bottom: 4px;
        }

        .track-info-val {
          font-size: 16px;
          font-weight: 600;
          color: var(--primary-dark);
        }

        .status-pill {
          display: inline-block;
          background: rgba(184, 155, 65, 0.12);
          color: var(--gold);
          border: 1px solid rgba(184, 155, 65, 0.3);
          padding: 6px 16px;
          border-radius: 99px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .status-pill.approved {
          background: #ecfdf5;
          color: #047857;
          border-color: #a7f3d0;
        }

        .status-pill.rejected {
          background: #fef2f2;
          color: #b91c1c;
          border-color: #fecaca;
        }

        /* Timeline Roadmap */
        .track-progress-bar-container {
          background: #f1f5f9;
          height: 6px;
          border-radius: 99px;
          margin-bottom: 32px;
          overflow: hidden;
        }

        .track-progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--primary) 0%, var(--gold) 100%);
          transition: width 0.6s ease;
        }

        .track-timeline {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }

        .track-step {
          text-align: center;
          position: relative;
        }

        .track-step-num {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          margin: 0 auto 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .track-step.done .track-step-num {
          background: var(--primary);
          color: #ffffff;
        }

        .track-step.active .track-step-num {
          background: #ffffff;
          border: 2px solid var(--gold);
          color: var(--gold);
          box-shadow: 0 0 0 4px rgba(184, 155, 65, 0.15);
        }

        .track-step.pending .track-step-num {
          background: #f1f5f9;
          color: #94a3b8;
        }

        .track-step-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--primary-dark);
          margin-bottom: 4px;
        }

        .track-step-date {
          font-size: 12px;
          color: var(--text-muted);
        }

        .track-notes-box {
          background: #f8fafc;
          border-left: 3px solid var(--gold);
          padding: 16px 20px;
          border-radius: 8px;
          font-size: 14px;
          color: var(--primary-dark);
          margin-bottom: 24px;
        }

        .track-result-footer {
          background: var(--cream);
          border-radius: 12px;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }

        /* ── FAQ SECTION ("Need Help Finding Your Application?") ── */
        .track-faq-section {
          max-width: 1100px;
          margin: 40px auto 80px;
          padding: 0 24px;
        }

        .track-faq-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .track-faq-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2rem, 3.5vw, 2.8rem);
          font-weight: 400;
          color: var(--primary-dark);
          margin: 0 0 10px;
        }

        .track-faq-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .track-faq-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 32px 24px;
          border: 1px solid rgba(184, 155, 65, 0.2);
          box-shadow: 0 10px 25px -5px rgba(26, 49, 61, 0.04);
          transition: transform 0.3s ease;
        }

        .track-faq-card:hover {
          transform: translateY(-4px);
          border-color: var(--gold);
        }

        .track-faq-card-title {
          font-size: 17px;
          font-weight: 600;
          color: var(--primary-dark);
          margin: 0 0 12px;
          line-height: 1.3;
        }

        .track-faq-card-desc {
          font-size: 14px;
          color: var(--text-muted);
          line-height: 1.6;
          margin: 0;
        }

        @media (max-width: 900px) {
          .track-faq-grid {
            grid-template-columns: 1fr;
          }
          .track-timeline {
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
          }
          .track-search-card {
            flex-direction: column;
            gap: 8px;
            padding: 12px;
          }
          .track-submit-btn, .track-input {
            width: 100%;
          }
          .track-trust-badges {
            gap: 16px;
          }
        }
      `}</style>

      <div className="header-margin"></div>
      <DefaultHeader />

      {/* HERO SECTION */}
      <section className="track-hero">
        <div className="track-hero-container">
          <div className="track-eyebrow">
            <span>●</span> {t("trackEyebrow", "Real-Time Application Tracker")}
          </div>

          <h1 className="track-hero-title">
            {t("trackHeroTitle1", "Track Your Visa")}{" "}
            <em>{t("trackHeroTitleWord", "Application")}</em>
          </h1>

          <p className="track-hero-subtitle">
            {t(
              "trackHeroSubtitle",
              "Enter your application reference code below for real-time embassy updates, status verification, and processing estimates."
            )}
          </p>

          <form onSubmit={handleSearch} className="track-search-card">
            <input
              type="text"
              className="track-input"
              placeholder={t(
                "trackInputPlaceholder",
                "e.g. VSA-2026-0813-ABCD or Passport Number"
              )}
              value={referenceNum}
              onChange={(e) => setReferenceNum(e.target.value)}
              required
            />
            <button
              type="submit"
              className="track-submit-btn"
              disabled={loading}
            >
              {loading
                ? t("trackSearchingBtn", "Searching...")
                : t("trackSubmitBtn", "Track Status")}
            </button>
          </form>

          {errorMsg && <div className="track-error-box">{errorMsg}</div>}

          {/* TRUST BADGES */}
          <div className="track-trust-badges">
            <span className="track-badge-item">
              <svg className="track-badge-icon" viewBox="0 0 24 24">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              {t("trackBadgeEncrypted", "256-Bit Encrypted")}
            </span>

            <span className="track-badge-item">
              <svg className="track-badge-icon" viewBox="0 0 24 24">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
              {t("trackBadgeEmbassySync", "Direct Embassy Sync")}
            </span>

            <span className="track-badge-item">
              <svg className="track-badge-icon" viewBox="0 0 24 24">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              {t("trackBadgeSupport", "24/7 Priority Support")}
            </span>
          </div>
        </div>
      </section>

      {/* DYNAMIC RESULT CARD */}
      {trackData && (
        <section className="track-result-section">
          <div className="track-result-card">
            {/* Header info */}
            <div className="track-result-header">
              <div>
                <div className="track-info-label">
                  {t("trackRefCodeLabel", "Reference Number")}
                </div>
                <div className="track-info-val">
                  {trackData.reference_number}
                </div>
              </div>
              <div>
                <div className="track-info-label">
                  {t("trackApplicantLabel", "Applicant")}
                </div>
                <div className="track-info-val">
                  {trackData.applicant_name}
                </div>
              </div>
              <div>
                <div className="track-info-label">
                  {t("trackDestinationLabel", "Visa Package")}
                </div>
                <div className="track-info-val">{trackData.visa_pack}</div>
              </div>
              <div>
                <div className="track-info-label">
                  {t("trackStatusLabel", "Status")}
                </div>
                <span className={`status-pill ${trackData.status}`}>
                  {trackData.status_label}
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="track-progress-bar-container">
              <div
                className="track-progress-bar-fill"
                style={{ width: `${trackData.progress}%` }}
              ></div>
            </div>

            {/* Timeline Roadmap */}
            <div className="track-timeline">
              {trackData.timeline.map((step) => (
                <div key={step.id} className={`track-step ${step.status}`}>
                  <div className="track-step-num">
                    {step.status === "done" ? "✓" : step.id}
                  </div>
                  <div className="track-step-title">{step.title}</div>
                  <div className="track-step-date">{step.date}</div>
                </div>
              ))}
            </div>

            {/* Admin Notes if available */}
            {trackData.admin_notes && (
              <div className="track-notes-box">
                <strong>Official Note: </strong> {trackData.admin_notes}
              </div>
            )}

            {/* Card Footer */}
            <div className="track-result-footer">
              <span style={{ fontSize: "14px", color: "var(--text-muted)" }}>
                {t("trackEstCompletion", "Estimated Completion:")}{" "}
                <strong style={{ color: "var(--primary-dark)" }}>
                  {trackData.estimated_date}
                </strong>
              </span>
              <button
                className="track-submit-btn"
                style={{ padding: "10px 20px", fontSize: "13px" }}
                onClick={() => window.print()}
              >
                {t("trackDownloadBtn", "Print Summary")}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* FAQ / NEED HELP SECTION */}
      <section className="track-faq-section">
        <div className="track-faq-header">
          <h2 className="track-faq-title">
            {t("trackFaqMainTitle", "Need Help Finding Your Application?")}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "15px" }}>
            {t(
              "trackFaqMainSubtitle",
              "Here are the most common questions regarding visa tracking and reference numbers."
            )}
          </p>
        </div>

        <div className="track-faq-grid">
          <div className="track-faq-card">
            <h3 className="track-faq-card-title">
              {t("trackFaqQ1Title", "Where can I find my Reference Code?")}
            </h3>
            <p className="track-faq-card-desc">
              {t(
                "trackFaqQ1Desc",
                "Your reference code (e.g. VSA-2026-XXXX) was sent to your email upon application submission and is printed on your receipt."
              )}
            </p>
          </div>

          <div className="track-faq-card">
            <h3 className="track-faq-card-title">
              {t("trackFaqQ2Title", "How often is the status updated?")}
            </h3>
            <p className="track-faq-card-desc">
              {t(
                "trackFaqQ2Desc",
                "Status updates occur in real-time as soon as our visa specialists or the embassy update your file."
              )}
            </p>
          </div>

          <div className="track-faq-card">
            <h3 className="track-faq-card-title">
              {t("trackFaqQ3Title", "Lost your reference number?")}
            </h3>
            <p className="track-faq-card-desc">
              {t(
                "trackFaqQ3Desc",
                "Contact our 24/7 support team with your passport number and full name to retrieve your application status."
              )}
            </p>
          </div>
        </div>
      </section>

      <DefaultFooter />
    </div>
  );
};

export default TrackVisa;